/**
 * Servicio para conectar con APIs de Analytics del Backend
 * Corresponde a los 47 endpoints operativos de FASE 2
 */

import { apiClient } from "./apiClient";

// Cliente API centralizado con interceptores JWT (evita hardcode de URL)
const api = apiClient;

// Interfaces de respuesta (correspondientes al backend)
export interface GlobalAnalysisResponse {
  productCountComparison: {
    odoo16: number;
    odoo18: number;
    growthPercentage: number;
  };
  topProductsOdoo16: Array<{
    productId: number;
    productName: string;
    categoryName: string;
    totalSales: number;
    orderCount: number;
    avgOrderValue: number;
  }>;
  topProductsOdoo18: Array<{
    productId: number;
    productName: string;
    categoryName: string;
    totalSales: number;
    orderCount: number;
    avgOrderValue: number;
  }>;
  summary: {
    totalProductsGrowth: number;
    avgGrowthPercentage: number;
    topGrowthProduct: string;
    biggestDeclineProduct: string;
  };
}

export interface StoreAnalysisResponse {
  stores: Array<{
    storeName: string;
    storeCode: string;
    totalSales16: number;
    totalSales18: number;
    growthPercentage: number;
    orderCount16: number;
    orderCount18: number;
    avgOrderValue16: number;
    avgOrderValue18: number;
    uniqueProducts16: number;
    uniqueProducts18: number;
    posCount: number;
    topPerformingPOS: string;
    ranking: number;
  }>;
  summary: {
    totalStores: number;
    avgGrowthPercentage: number;
    topPerformingStore: string;
    worstPerformingStore: string;
    totalSalesGrowth: number;
  };
}

export interface UserAnalysisResponse {
  users: Array<{
    userId: number;
    userName: string;
    userLogin: string;
    totalSales16: number;
    totalSales18: number;
    growthPercentage: number;
    orderCount16: number;
    orderCount18: number;
    avgOrderValue16: number;
    avgOrderValue18: number;
    productsSold16: number;
    productsSold18: number;
    ranking: number;
    performanceScore: number;
  }>;
  summary: {
    totalUsers: number;
    avgGrowthPercentage: number;
    topPerformingUser: string;
    worstPerformingUser: string;
    totalSalesGrowth: number;
  };
}

export interface PosAnalysisResponse {
  analysis: Array<{
    userId: number;
    userName: string;
    userLogin: string;
    totalSales16: number;
    totalSales18: number;
    growthPercentage: number;
    orderCount16: number;
    orderCount18: number;
    avgOrderValue16: number;
    avgOrderValue18: number;
    productsSold16: number;
    productsSold18: number;
    ranking: number;
    efficiencyScore: number;
  }>;
  summary: {
    totalOperators: number;
    avgEfficiencyScore: number;
    topOperator: string;
    worstOperator: string;
    totalSalesGrowth: number;
  };
}

// ===========================
// DASHBOARD LEVEL 1 INTERFACES
// ===========================

export interface MonthlyMetrics {
  month: string;
  year: number;
  salesAmount: number;
  unitsSold: number;
  ordersCount: number;
  // null cuando no hay datos del año anterior para comparar (antes asignaba 100% arbitrario)
  growthPercentage: number | null;
  previousYearSalesAmount?: number;
  previousYearUnitsSold?: number;
  grossSales?: number;
  totalDiscounts?: number;
  totalReturns?: number;
  // Campos para comisiones
  totalCommissions?: number; // Total de comisiones calculadas
  realSalesAmount?: number; // Ventas reales después de comisiones
  avgDiscountPercentage?: number; // Porcentaje promedio de descuento
  // Campos para proyeccion proporcional del mes actual
  isCurrentMonth?: boolean;
  daysElapsed?: number;
  daysInMonth?: number;
  // Fecha exacta del último dato del mes (YYYY-MM-DD) — para mostrar "Datos hasta DD/MM"
  lastDataDate?: string;
  projectedSales?: number;
  projectedUnits?: number;
}

export interface TopProductMonthly {
  productId: number;
  productName: string | { es_MX: string; en_US: string };
  categoryName: string;
  totalSales: number; // Ventas netas (después de descuentos y devoluciones)
  unitsSold: number;
  avgPrice: number;
  ordersCount: number;
  ranking: number;
  growthPercentage?: number; // Crecimiento año sobre año vs mismo mes del año anterior
  // Campos adicionales para análisis detallado
  grossSales?: number; // Ventas brutas
  totalDiscounts?: number; // Total de descuentos aplicados
  totalReturns?: number; // Total de devoluciones
  previousYearSales?: number; // Ventas del año anterior para referencia
  // Campos para comisiones
  totalCommissions?: number; // Total de comisiones por este producto
  realTotalSales?: number; // Ventas reales después de comisiones
  avgDiscountPercentage?: number; // Porcentaje promedio de descuento aplicado
}

// B4: semántica de RANGO. Las claves *Last3Months se conservan por
// backward-compat pero describen el período completo seleccionado:
//   totalSalesLast3Months  = total del período (suma de todos los meses del rango)
//   totalUnitsLast3Months  = unidades del período
//   avgGrowthLast3Months   = crecimiento promedio del período
//   totalSalesCurrentMonth = último mes del rango
//   totalUnitsCurrentMonth = unidades del último mes del rango
//   currentMonthGrowth     = crecimiento del último mes del rango
export interface DashboardLevel1Summary {
  totalSalesLast3Months: number;
  totalSalesCurrentMonth: number;
  totalUnitsLast3Months: number;
  totalUnitsCurrentMonth: number;
  avgGrowthLast3Months: number;
  currentMonthGrowth: number;
}

export interface StoreMetrics {
  storeName: string;
  totalSales: number;
  growthPercentage?: number | undefined;
  // "pos" = tienda física POS · "vendor" = vendedor SO. Permite separar visualmente en UI.
  storeType?: "pos" | "vendor";
  unitsSold: number;
  ordersCount: number;
  ranking: number;
}

export interface DashboardLevel1Metrics {
  monthlyData: MonthlyMetrics[];
  topProductsCurrentMonth: TopProductMonthly[];
  topStores: StoreMetrics[];
  summary: DashboardLevel1Summary;
}

/**
 * Clase principal del servicio de Analytics
 */
export class AnalyticsService {
  // ===========================
  // GLOBAL ANALYTICS (NIVEL 1)
  // ===========================

  /**
   * Obtiene análisis global de productos
   */
  static async getGlobalAnalysis(): Promise<GlobalAnalysisResponse> {
    const response = await api.get<GlobalAnalysisResponse>(
      "/analytics/global/overview",
    );
    return response.data;
  }

  /**
   * Obtiene tendencias globales de productos
   */
  static async getGlobalTrends(): Promise<any> {
    const response = await api.get("/analytics/global/trends");
    return response.data;
  }

  /**
   * Obtiene comparación de catálogos Odoo 16 vs 18
   */
  static async getCatalogComparison(): Promise<any> {
    const response = await api.get("/analytics/global/catalog-comparison");
    return response.data;
  }

  // ===========================
  // STORE ANALYTICS (NIVEL 2)
  // ===========================

  /**
   * Obtiene análisis por tiendas
   */
  static async getStoresAnalysis(): Promise<StoreAnalysisResponse> {
    const response = await api.get<StoreAnalysisResponse>(
      "/analytics/stores/overview",
    );
    return response.data;
  }

  /**
   * Obtiene productos de una tienda específica
   */
  static async getStoreProducts(storeName: string): Promise<any> {
    const response = await api.get(
      `/analytics/stores/products/${encodeURIComponent(storeName)}`,
    );
    return response.data;
  }

  /**
   * Obtiene comparación entre tiendas
   */
  static async getStoresComparison(): Promise<any> {
    const response = await api.get("/analytics/stores/comparison");
    return response.data;
  }

  // ===========================
  // POS ANALYTICS (NIVEL 3)
  // ===========================

  /**
   * Obtiene análisis POS general
   */
  static async getPosAnalysis(): Promise<PosAnalysisResponse> {
    const response = await api.get<PosAnalysisResponse>(
      "/analytics/pos/overview",
    );
    return response.data;
  }

  /**
   * Obtiene eficiencia de operadores POS
   */
  static async getPosEfficiency(): Promise<any> {
    const response = await api.get("/analytics/pos/efficiency");
    return response.data;
  }

  /**
   * Obtiene ventas por operador específico
   */
  static async getOperatorSales(operatorId: number): Promise<any> {
    const response = await api.get(`/analytics/pos/operator/${operatorId}`);
    return response.data;
  }

  /**
   * Obtiene comparación de turnos POS
   */
  static async getShiftComparison(): Promise<any> {
    const response = await api.get("/analytics/pos/shifts");
    return response.data;
  }

  // ===========================
  // USER ANALYTICS (NIVEL 4)
  // ===========================

  /**
   * Obtiene análisis de usuarios
   */
  static async getUsersAnalysis(): Promise<UserAnalysisResponse> {
    const response = await api.get<UserAnalysisResponse>(
      "/analytics/users/overview",
    );
    return response.data;
  }

  /**
   * Obtiene productividad de usuarios
   */
  static async getUserProductivity(): Promise<any> {
    const response = await api.get("/analytics/users/productivity");
    return response.data;
  }

  /**
   * Obtiene métricas de un usuario específico
   */
  static async getUserMetrics(userId: number): Promise<any> {
    const response = await api.get(`/analytics/users/metrics/${userId}`);
    return response.data;
  }

  /**
   * Obtiene ranking de usuarios
   */
  static async getUserRanking(): Promise<any> {
    const response = await api.get("/analytics/users/ranking");
    return response.data;
  }

  /**
   * Obtiene comparación de rendimiento de usuarios
   */
  static async getUserPerformance(): Promise<any> {
    const response = await api.get("/analytics/users/performance");
    return response.data;
  }

  /**
   * Obtiene análisis de crecimiento de usuarios
   */
  static async getUserGrowth(): Promise<any> {
    const response = await api.get("/analytics/users/growth");
    return response.data;
  }

  // ===========================
  // MULTI-LEVEL ANALYTICS
  // ===========================

  /**
   * Obtiene dashboard principal con todas las métricas
   */
  static async getDashboardOverview(): Promise<{
    global: GlobalAnalysisResponse;
    stores: StoreAnalysisResponse;
    users: UserAnalysisResponse;
    pos: PosAnalysisResponse;
  }> {
    const [global, stores, users, pos] = await Promise.all([
      this.getGlobalAnalysis(),
      this.getStoresAnalysis(),
      this.getUsersAnalysis(),
      this.getPosAnalysis(),
    ]);

    return { global, stores, users, pos };
  }

  /**
   * Obtiene métricas mensuales para Dashboard Nivel 1 (Odoo 18).
   * Bloque 10 plan dashboard_ux_ventas_margenes 2026-05-21:
   *   - opts opcional con dateRange { start, end } (ISO YYYY-MM-DD) y storeId.
   *   - Si dateRange presente, propaga startMonth/endMonth al backend.
   *   - Backward-compat: sin opts el endpoint usa default (últimos 4 meses).
   */
  static async getDashboardMetrics(opts?: {
    startMonth?: string | null;
    endMonth?: string | null;
    storeName?: string | null;
  }): Promise<DashboardLevel1Metrics> {
    try {
      const params: Record<string, string> = {};
      if (opts?.startMonth) params["startMonth"] = opts.startMonth;
      if (opts?.endMonth) params["endMonth"] = opts.endMonth;
      if (opts?.storeName) params["storeName"] = opts.storeName;
      const response = await api.get<DashboardLevel1Metrics>(
        "/analytics/global/dashboard-metrics",
        { params },
      );
      return response.data;
    } catch (error) {
      console.error("Error getting dashboard metrics:", error);
      throw new Error("Failed to fetch dashboard metrics");
    }
  }

  /**
   * NUEVO: Obtiene datos específicos de un mes (productos y tiendas)
   */
  static async getMonthlyData(year: number, month: number): Promise<any> {
    const response = await api.get(`/analytics/global/month/${year}/${month}`);
    return response.data;
  }

  // ===========================
  // UTILITIES
  // ===========================

  /**
   * Formatea números para display
   */
  static formatNumber(value: number): string {
    return new Intl.NumberFormat("es-MX", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /**
   * Formatea porcentajes
   */
  static formatPercentage(value: number): string {
    return new Intl.NumberFormat("es-MX", {
      style: "percent",
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
    }).format(value / 100);
  }

  /**
   * Formatea moneda
   */
  static formatCurrency(value: number): string {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(value);
  }

  /**
   * Extrae el nombre apropiado de un objeto multiidioma o string
   */
  static extractProductName(
    productName: string | { es_MX: string; en_US: string },
  ): string {
    if (typeof productName === "string") {
      return productName;
    }
    // Priorizar español mexicano, luego inglés
    return productName.es_MX || productName.en_US || "Producto sin nombre";
  }
}

export default AnalyticsService;
